/**
 * Invite dialog for the owner sub-delegate management page — Feature 075
 * (plan 013, C-T05). Validates email + names + at least one permission.
 */

import { useState, type FormEvent } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import {
  PermissionToggleGroup,
  type DelegationPermission,
} from '@/shared/components/sub-delegation';
import type { InviteDelegatePayload } from '../../types/subDelegateManagement';

type InviteDelegateDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isSubmitting: boolean;
  errorMessage: string | null;
  onSubmit: (payload: InviteDelegatePayload) => Promise<boolean>;
};

const inputClass =
  'w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-[#026857] focus:outline-none focus:ring-1 focus:ring-[#026857] disabled:bg-gray-100';

export function InviteDelegateDialog({
  open,
  onOpenChange,
  isSubmitting,
  errorMessage,
  onSubmit,
}: InviteDelegateDialogProps) {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [permissions, setPermissions] = useState<DelegationPermission[]>([
    'MESSAGING',
  ]);

  const resetForm = () => {
    setEmail('');
    setFirstName('');
    setLastName('');
    setPermissions(['MESSAGING']);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (permissions.length === 0) return;

    const succeeded = await onSubmit({
      email: email.trim(),
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      permissions,
    });

    if (succeeded) {
      resetForm();
      onOpenChange(false);
    }
  };

  const canSubmit =
    !isSubmitting &&
    email.trim().length > 0 &&
    firstName.trim().length > 0 &&
    lastName.trim().length > 0 &&
    permissions.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-label="Invite a sub-delegate">
        <DialogHeader>
          <DialogTitle>Invite a sub-delegate</DialogTitle>
          <DialogDescription>
            They will receive an email invitation to join your workspace with
            the permissions you choose. The invitation expires in 7 days.
          </DialogDescription>
        </DialogHeader>

        <form
          onSubmit={handleSubmit}
          className="space-y-4"
          aria-label="Invite a new sub-delegate"
        >
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-gray-700">
              Email
            </span>
            <input
              type="email"
              required
              placeholder="email@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isSubmitting}
              className={inputClass}
              aria-label="Email"
            />
          </label>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-gray-700">
                First name
              </span>
              <input
                type="text"
                required
                placeholder="First name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                disabled={isSubmitting}
                className={inputClass}
                aria-label="First name"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-gray-700">
                Last name
              </span>
              <input
                type="text"
                required
                placeholder="Last name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                disabled={isSubmitting}
                className={inputClass}
                aria-label="Last name"
              />
            </label>
          </div>

          <div>
            <span className="mb-1.5 block text-xs font-medium text-gray-700">
              Permissions
            </span>
            <PermissionToggleGroup
              value={permissions}
              onChange={setPermissions}
              disabled={isSubmitting}
              idPrefix="invite-permission"
            />
          </div>

          {errorMessage && (
            <p className="text-xs text-red-600" role="alert">
              {errorMessage}
            </p>
          )}

          <DialogFooter>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="rounded-md bg-[#026857] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#025246] disabled:opacity-50"
            >
              {isSubmitting ? 'Sending invitation…' : 'Send invitation'}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
